import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { useSelector } from "react-redux";
import { callApi } from "../../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../../ApiHelper/ApiUrl";
import { RoutesLink } from "../../../../ApiHelper/RoutesLink";
import {
  CalculateDuration,
  GenderHandler,
  UserTypeHandler,
  employeeStatusHandler,
} from "../../../../utility/CommonFunctions";
import ViewDocumentModal from "../../../Sites/ViewDocumentModal";
import DocumentIcon from "../../../../components/Icons/DocumentIcon";
import EditIcon from "../../../../components/Icons/EditIcon";
import UserTransferIcon from "../../../../components/Icons/UserTransferIcon";
import BankIcon from "../../../../components/Icons/BankIcon";
import LocationIcon from "../../../../components/Icons/LocationIcon";
import EmployeesIcon from "../../../../components/Icons/EmployeesIcon";
import SitesIcon from "../../../../components/Icons/SitesIcon";
import CustomersIcon from "../../../../components/Icons/CustomersIcon";
import OfficesIcon from "../../../../components/Icons/OfficesIcon";
import ViewIcon from "../../../../components/Icons/ViewIcon";
import BgBlurLoader from "../../../../components/Loaders/BgBlurLoader";
import MainHeading from "../../../../components/comman/MainHeading";

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state.root.userDetails);
  const [employeeDetail, setEmployeeDetail] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const toggleModal = () => {
    setModal(!modal);
  };

  useEffect(() => {
    if (id) {
      GetEmployeeDetail();
    }
  }, [id]);

  const GetEmployeeDetail = () => {
    let options = {
      method: "GET",
    };
    let API = apiUrl?.get_profile + `?userId=${id}`;
    setIsLoading(true);
    callApi(API, options).then((response) => {
      setIsLoading(false);
      if (response?.status === true) {
        setEmployeeDetail(response?.data);
      } else {
        toast.error(response?.message);
      }
    });
  };

  const handleDocumentClick = (item) => {
    const docUrl = {
      url: item?.url,
    };
    setSelectedDocument(docUrl);
    setModal(true);
  };

  const HandleUpdate = (data) => {
    navigate(RoutesLink?.add_employee_onboard, { state: { data: data } });
  };

  const EmployeeTransfer = (data) => {
    navigate(RoutesLink?.employee_transfer, { state: { data: data } });
  };

  const totalDocumentsCount = employeeDetail?.documents?.length || 0;
  const totalSitesCount = employeeDetail?.usersites?.length || 0;
  const totalCompaniesCount = employeeDetail?.usercompanies?.length || 0;
  const totalCustomersCount = employeeDetail?.usercustomers?.length || 0;
  const tenureDuration = employeeDetail?.joiningDate
    ? CalculateDuration(employeeDetail?.joiningDate)
    : "--";

  const TABS = [
    { id: "all", label: "All Overview", icon: "📑" },
    { id: "personal", label: "Personal & Family", icon: "👤" },
    { id: "employment", label: "Role & Permissions", icon: "💼" },
    { id: "statutory", label: "Statutory & Banking", icon: "🏦" },
    { id: "addresses", label: "Addresses", icon: "📍" },
    { id: "experience_docs", label: "Experience & Docs", icon: "📁" },
  ];

  const canManageEmployee =
    userDetails?.department?.name === "HRM" ||
    userDetails?.designation?.name === "Director";

  return (
    <>
      {isLoading && <BgBlurLoader />}
      <div className="content container-fluid pb-5">
        {/* Main Header with Actions */}
        <MainHeading title="Employee Detail" showBackButton={true}>
          {canManageEmployee && (
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-primary d-inline-flex align-items-center gap-2 px-3 py-1 fw-medium rounded-2"
                onClick={() => HandleUpdate(employeeDetail)}
              >
                <EditIcon />
                <span>Edit</span>
              </button>
              {employeeDetail?.type === 0 && (
                <button
                  type="button"
                  className="btn btn-outline-primary d-inline-flex align-items-center gap-2 px-3 py-1 fw-medium rounded-2"
                  onClick={() => EmployeeTransfer(employeeDetail)}
                >
                  <UserTransferIcon />
                  <span>Transfer User</span>
                </button>
              )}
            </div>
          )}
        </MainHeading>

        {/* Master Summary Card */}
        <div
          className="card border-0 shadow-sm rounded-3 mb-4"
          style={{ background: "#ffffff" }}
        >
          <div className="card-body p-4">
            {/* Top Identity Strip */}
            <div
              className="d-flex align-items-start justify-content-between flex-wrap gap-4 pb-3 border-bottom"
              style={{ borderColor: "#f1f5f9" }}
            >
              <div className="d-flex align-items-center gap-3">
                {/* Profile Photo */}
                <div
                  className="position-relative rounded-circle flex-shrink-0 cursor-pointer overflow-hidden border"
                  style={{
                    width: "80px",
                    height: "80px",
                    borderColor: "#e2e8f0",
                    padding: "2px",
                    background: "#ffffff",
                    boxShadow: "0 4px 14px rgba(37, 99, 235, 0.12)",
                    transition: "transform 0.2s ease",
                  }}
                  onClick={() => {
                    if (employeeDetail?.profilePhoto) {
                      handleDocumentClick({
                        url: employeeDetail?.profilePhoto,
                      });
                    }
                  }}
                  title="Click to preview full photo"
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "scale(1.04)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                >
                  <img
                    src={
                      employeeDetail?.profilePhoto || "/assets/img/employee.svg"
                    }
                    alt={employeeDetail?.name || "Profile Photo"}
                    className="w-100 h-100 rounded-circle"
                    style={{ objectFit: "cover" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/img/employee.svg";
                    }}
                  />
                </div>

                <div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h3
                      className="mb-0 fw-bold text-dark"
                      style={{ letterSpacing: "-0.02em", fontSize: "1.45rem" }}
                    >
                      {employeeDetail?.name || "--"}
                    </h3>

                    {/* Employee Code Pill */}
                    {employeeDetail?.employeeCode && (
                      <span
                        className="badge rounded-pill px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1 font-monospace"
                        style={{
                          fontSize: "12px",
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          border: "1px solid #bfdbfe",
                          letterSpacing: "0.03em",
                        }}
                      >
                        <span>🆔</span>
                        <span>{employeeDetail.employeeCode}</span>
                      </span>
                    )}

                    {/* Status Pill */}
                    {employeeDetail?.status ? (
                      employeeStatusHandler(employeeDetail.status)
                    ) : (
                      <span
                        className="badge rounded-pill px-2 py-1 fw-semibold bg-success-subtle text-success border border-success-subtle"
                        style={{ fontSize: "12px" }}
                      >
                        Active
                      </span>
                    )}

                    {/* User Type Badge */}
                    {employeeDetail?.type !== undefined && (
                      <span
                        className="badge rounded-pill px-2 py-1 fw-semibold"
                        style={{
                          fontSize: "12px",
                          background: "#f1f5f9",
                          color: "#334155",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {UserTypeHandler(employeeDetail.type) || "Staff"}
                      </span>
                    )}

                    {/* Skill Classification Badge */}
                    {employeeDetail?.skillType && (
                      <span
                        className="badge rounded-pill px-2 py-1 fw-semibold bg-info-subtle text-info border border-info-subtle"
                        style={{ fontSize: "12px" }}
                      >
                        {employeeDetail.skillType}
                      </span>
                    )}
                  </div>

                  {/* Subtitle Meta Strip */}
                  <div className="text-secondary small mt-1 d-flex align-items-center gap-3 flex-wrap">
                    {employeeDetail?.email && (
                      <span className="d-inline-flex align-items-center gap-1 text-secondary">
                        <span>✉️</span>
                        <span className="fw-medium text-dark">
                          {employeeDetail.email}
                        </span>
                      </span>
                    )}
                    {employeeDetail?.phone && (
                      <>
                        <span>•</span>
                        <span className="d-inline-flex align-items-center gap-1 text-secondary">
                          <span>📞</span>
                          <span className="fw-medium text-dark">
                            {employeeDetail.phone}
                          </span>
                        </span>
                      </>
                    )}
                    {employeeDetail?.Company?.name && (
                      <>
                        <span>•</span>
                        <span className="d-inline-flex align-items-center gap-1 text-secondary">
                          <span>🏢</span>
                          <span className="fw-medium text-dark">
                            {employeeDetail.Company.name}
                          </span>
                        </span>
                      </>
                    )}
                    {employeeDetail?.department?.name && (
                      <>
                        <span>•</span>
                        <span className="d-inline-flex align-items-center gap-1 text-secondary">
                          <span>📁</span>
                          <span className="fw-medium text-dark">
                            {employeeDetail.department.name}
                          </span>
                        </span>
                      </>
                    )}
                    {(employeeDetail?.site?.name ||
                      employeeDetail?.userdetail?.city) && (
                        <>
                          <span>•</span>
                          <span className="d-inline-flex align-items-center gap-1 text-secondary">
                            <span>📍</span>
                            <span className="fw-medium text-dark">
                              {employeeDetail?.site?.name ||
                                employeeDetail?.userdetail?.city}
                            </span>
                          </span>
                        </>
                      )}
                  </div>
                </div>
              </div>

              {/* Quick Quantitative Stats */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {/* Tenure */}
                <div
                  className="px-3 py-2 rounded-2 text-center"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    minWidth: "125px",
                  }}
                >
                  <div
                    className="text-secondary text-uppercase"
                    style={{
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Experience
                  </div>
                  <div
                    className="fw-bold text-dark text-truncate"
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.2",
                      marginTop: "2px",
                    }}
                    title={tenureDuration}
                  >
                    {tenureDuration}
                  </div>
                </div>

                {/* Shift */}
                <div
                  className="px-3 py-2 rounded-2 text-center"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    minWidth: "115px",
                  }}
                >
                  <div
                    className="text-secondary text-uppercase"
                    style={{
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Shift
                  </div>
                  <div
                    className="fw-bold text-dark text-truncate"
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.2",
                      marginTop: "2px",
                    }}
                    title={employeeDetail?.Shift?.shiftName || "General Shift"}
                  >
                    {employeeDetail?.Shift?.shiftName || "General"}
                  </div>
                </div>

                {/* Assigned Sites */}
                <div
                  className="px-3 py-2 rounded-2 text-center"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    minWidth: "105px",
                  }}
                >
                  <div
                    className="text-secondary text-uppercase"
                    style={{
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Sites
                  </div>
                  <div
                    className="fw-bold text-primary"
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.2",
                      marginTop: "2px",
                    }}
                  >
                    {totalSitesCount}
                  </div>
                </div>

                {/* Documents */}
                <div
                  className="px-3 py-2 rounded-2 text-center"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    minWidth: "105px",
                  }}
                >
                  <div
                    className="text-secondary text-uppercase"
                    style={{
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Documents
                  </div>
                  <div
                    className="fw-bold text-primary"
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.2",
                      marginTop: "2px",
                    }}
                  >
                    {totalDocumentsCount}
                  </div>
                </div>
              </div>
            </div>

            {/* Key Information Grid */}
            <div className="row g-3 pt-3">
              {/* Joining Date */}
              <div className="col-12 col-sm-6 col-lg-3">
                <div
                  className="p-3 rounded-2 h-100"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <div
                    className="text-muted text-uppercase mb-1"
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Joining Date
                  </div>
                  <div
                    className="fw-semibold text-dark text-truncate"
                    style={{ fontSize: "13.5px" }}
                  >
                    {employeeDetail?.joiningDate
                      ? moment(employeeDetail.joiningDate).format("DD-MMM-YYYY")
                      : "--"}
                  </div>
                  <div
                    className="text-secondary small mt-1"
                    style={{ fontSize: "11px" }}
                  >
                    {employeeDetail?.joiningDate
                      ? `${moment(employeeDetail.joiningDate).fromNow()}`
                      : "Official Onboarding"}
                  </div>
                </div>
              </div>

              {/* Designation */}
              <div className="col-12 col-sm-6 col-lg-3">
                <div
                  className="p-3 rounded-2 h-100"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <div
                    className="text-muted text-uppercase mb-1"
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Designation
                  </div>
                  <div
                    className="fw-semibold text-dark text-truncate"
                    title={
                      employeeDetail?.designationName ||
                      employeeDetail?.designation?.name
                    }
                    style={{ fontSize: "13.5px" }}
                  >
                    {employeeDetail?.designationName ||
                      employeeDetail?.designation?.name ||
                      "--"}
                  </div>
                  <div
                    className="text-secondary small mt-1"
                    style={{ fontSize: "11px" }}
                  >
                    {employeeDetail?.department?.name
                      ? `Dept: ${employeeDetail.department.name}`
                      : "Assigned Function"}
                  </div>
                </div>
              </div>

              {/* Date of Birth & Blood Group */}
              <div className="col-12 col-sm-6 col-lg-3">
                <div
                  className="p-3 rounded-2 h-100"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <div
                    className="text-muted text-uppercase mb-1"
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Date of Birth
                  </div>
                  <div
                    className="fw-semibold text-dark text-truncate"
                    style={{ fontSize: "13.5px" }}
                  >
                    {employeeDetail?.dob
                      ? moment(employeeDetail.dob).format("DD-MMM-YYYY")
                      : "--"}
                  </div>
                  <div
                    className="text-secondary small mt-1"
                    style={{ fontSize: "11px" }}
                  >
                    {employeeDetail?.bloodGroup
                      ? `Blood Group: ${employeeDetail.bloodGroup}`
                      : "Personal Record"}
                  </div>
                </div>
              </div>

              {/* Marital Status & Family */}
              <div className="col-12 col-sm-6 col-lg-3">
                <div
                  className="p-3 rounded-2 h-100"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <div
                    className="text-muted text-uppercase mb-1"
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Marital Status
                  </div>
                  <div
                    className="fw-semibold text-dark text-truncate"
                    style={{ fontSize: "13.5px" }}
                  >
                    {employeeDetail?.isMarried ? "Married" : "Unmarried"}
                  </div>
                  <div
                    className="text-secondary small mt-1"
                    style={{ fontSize: "11px" }}
                  >
                    {employeeDetail?.gender !== undefined
                      ? `Gender: ${GenderHandler(employeeDetail.gender)}`
                      : "Family Record"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Navigation Tabs */}
        <div
          className="d-flex align-items-center gap-2 mb-4 overflow-x-auto pb-1"
          style={{ scrollbarWidth: "none" }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`btn btn-sm rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-2 text-nowrap transition-all ${isActive
                  ? "btn-primary shadow-sm"
                  : "btn-light text-secondary border bg-white"
                  }`}
                style={{
                  fontSize: "13px",
                  borderColor: isActive ? "transparent" : "#e2e8f0",
                  transition: "all 0.2s ease",
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* SECTION 1: Personal & Family Details */}
        {(activeTab === "all" || activeTab === "personal") && (
          <div
            className="card border-0 shadow-sm rounded-3 mb-4 overflow-hidden"
            style={{ background: "#ffffff" }}
          >
            <div
              className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom"
              style={{ borderColor: "#f1f5f9" }}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-2 p-1 d-flex align-items-center justify-content-center"
                  style={{
                    background: "#eff6ff",
                    color: "#2563eb",
                    width: "32px",
                    height: "32px",
                  }}
                >
                  <EmployeesIcon />
                </div>
                <div>
                  <h5
                    className="mb-0 fw-bold text-dark"
                    style={{ fontSize: "15px" }}
                  >
                    Personal & Family Information
                  </h5>
                  <p
                    className="mb-0 text-muted small"
                    style={{ fontSize: "11.5px" }}
                  >
                    Basic employee demographic and family background details
                  </p>
                </div>
              </div>
              <span
                className="badge bg-light text-secondary border px-3 py-1 rounded-pill"
                style={{ fontSize: "12px", fontWeight: 600 }}
              >
                Identity
              </span>
            </div>

            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Employee Code
                    </div>
                    <div
                      className="fw-semibold text-dark font-monospace"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.employeeCode || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Full Name
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.name || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Gender
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {GenderHandler(employeeDetail?.gender)}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Date of Birth
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.dob
                        ? moment(employeeDetail.dob).format("DD-MMM-YYYY")
                        : "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Primary Email
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      title={employeeDetail?.email}
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.email || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Contact Number
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.phone || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Father's Name
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.userdetail?.fatherName || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Mother's Name
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.userdetail?.motherName || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Blood Group
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.bloodGroup || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Marital Status
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.isMarried ? "Married" : "Unmarried"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Skill Category
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.skillType || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Employment Status
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.status || "--"}
                    </div>
                  </div>
                </div>

                {/* Exit Date / Last Working Day if applicable */}
                {(employeeDetail?.status === "exEmployee" || employeeDetail?.exitDate) && (
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div
                      className="p-3 rounded-2 h-100"
                      style={{ background: "#fff1f2", border: "1px solid #fecdd3" }}
                    >
                      <div
                        className="text-danger text-uppercase mb-1"
                        style={{ fontSize: "11px", fontWeight: 700 }}
                      >
                        Last Working Day
                      </div>
                      <div
                        className="fw-semibold text-danger"
                        style={{ fontSize: "13.5px" }}
                      >
                        {employeeDetail?.exitDate
                          ? moment(employeeDetail.exitDate).format("DD-MMM-YYYY")
                          : "--"}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Nominee Details Card Inside Personal */}
              <div
                className="mt-4 p-3 rounded-3 border"
                style={{
                  background: "#fbfcfe",
                  borderColor: "#e2e8f0",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6
                    className="mb-0 fw-bold text-dark d-flex align-items-center gap-2"
                    style={{ fontSize: "14px" }}
                  >
                    <span>🛡️</span>
                    <span>Nominee & Beneficiary Information</span>
                  </h6>
                  <span
                    className="badge bg-light text-secondary border px-2 py-1 rounded-pill"
                    style={{ fontSize: "11px" }}
                  >
                    {employeeDetail?.userdetail?.nomineeRelation || "Beneficiary"}
                  </span>
                </div>

                <div className="row g-3">
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="small text-muted text-uppercase fw-bold mb-1" style={{ fontSize: "11px" }}>
                      Nominee Name
                    </div>
                    <div className="fw-semibold text-dark text-capitalize" style={{ fontSize: "13.5px" }}>
                      {employeeDetail?.userdetail?.nomineeName || "--"}
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="small text-muted text-uppercase fw-bold mb-1" style={{ fontSize: "11px" }}>
                      Relation
                    </div>
                    <div className="fw-semibold text-dark" style={{ fontSize: "13.5px" }}>
                      {employeeDetail?.userdetail?.nomineeRelation || "--"}
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="small text-muted text-uppercase fw-bold mb-1" style={{ fontSize: "11px" }}>
                      Contact Number
                    </div>
                    <div className="fw-semibold text-dark" style={{ fontSize: "13.5px" }}>
                      {employeeDetail?.userdetail?.nomineePhone || "--"}
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="small text-muted text-uppercase fw-bold mb-1" style={{ fontSize: "11px" }}>
                      Nominee Date of Birth
                    </div>
                    <div className="fw-semibold text-dark" style={{ fontSize: "13.5px" }}>
                      {employeeDetail?.userdetail?.nomineeDob
                        ? moment(employeeDetail.userdetail.nomineeDob).format("DD-MMM-YYYY")
                        : "--"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 2: Employment, Permissions & Assignments */}
        {(activeTab === "all" || activeTab === "employment") && (
          <div
            className="card border-0 shadow-sm rounded-3 mb-4 overflow-hidden"
            style={{ background: "#ffffff" }}
          >
            <div
              className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom"
              style={{ borderColor: "#f1f5f9" }}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-2 p-1 d-flex align-items-center justify-content-center"
                  style={{
                    background: "#f0fdf4",
                    color: "#16a34a",
                    width: "32px",
                    height: "32px",
                  }}
                >
                  <OfficesIcon />
                </div>
                <div>
                  <h5
                    className="mb-0 fw-bold text-dark"
                    style={{ fontSize: "15px" }}
                  >
                    Employment, Shift & Access Permissions
                  </h5>
                  <p
                    className="mb-0 text-muted small"
                    style={{ fontSize: "11.5px" }}
                  >
                    Organizational placement, role permissions, and assigned operations
                  </p>
                </div>
              </div>
              <span
                className="badge bg-light text-secondary border px-3 py-1 rounded-pill"
                style={{ fontSize: "12px", fontWeight: 600 }}
              >
                Governance
              </span>
            </div>

            <div className="card-body p-4">
              <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      On-Roll Company
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      title={employeeDetail?.Company?.name}
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.Company?.name || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Employee Type
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {UserTypeHandler(employeeDetail?.type) || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Department
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      title={employeeDetail?.department?.name}
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.department?.name || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Designation Name
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      title={employeeDetail?.designationName}
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.designationName || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      System Role Permission
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      title={employeeDetail?.designation?.name}
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.designation?.name || "--"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Assigned Shift
                    </div>
                    <div
                      className="fw-semibold text-dark text-truncate"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.Shift?.shiftName || "General Shift"}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <div
                    className="p-3 rounded-2 h-100"
                    style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                  >
                    <div
                      className="text-muted text-uppercase mb-1"
                      style={{ fontSize: "11px", fontWeight: 700 }}
                    >
                      Assigned Site Type
                    </div>
                    <div
                      className="fw-semibold text-dark"
                      style={{ fontSize: "13.5px" }}
                    >
                      {employeeDetail?.assignedSiteType || "--"}
                    </div>
                  </div>
                </div>

                {employeeDetail?.type === 1 && (
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div
                      className="p-3 rounded-2 h-100"
                      style={{ background: "#f8fafc", border: "1px solid #f1f5f9" }}
                    >
                      <div
                        className="text-muted text-uppercase mb-1"
                        style={{ fontSize: "11px", fontWeight: 700 }}
                      >
                        Assigned Office
                      </div>
                      <div
                        className="fw-semibold text-dark text-capitalize text-truncate"
                        style={{ fontSize: "13.5px" }}
                      >
                        {employeeDetail?.site?.name || "--"}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Operational Assignments Cards Row */}
              <div className="row g-3">
                {/* Assigned Companies (if applicable) */}
                {employeeDetail?.type === 1 && (
                  <div className="col-12 col-md-4">
                    <div
                      className="card h-100 border rounded-3 overflow-hidden"
                      style={{ borderColor: "#e2e8f0", background: "#fbfcfe" }}
                    >
                      <div className="card-header bg-white border-bottom py-2 px-3 d-flex align-items-center justify-content-between">
                        <span className="fw-bold text-dark small text-uppercase d-flex align-items-center gap-1">
                          <span>🏛️</span>
                          <span>Assigned Companies</span>
                        </span>
                        <span className="badge rounded-pill bg-light text-secondary border">
                          {totalCompaniesCount}
                        </span>
                      </div>
                      <div
                        className="card-body p-3"
                        style={{ maxHeight: "240px", overflowY: "auto" }}
                      >
                        {employeeDetail?.usercompanies?.length > 0 ? (
                          <div className="d-flex flex-column gap-2">
                            {employeeDetail.usercompanies.map((item, idx) => (
                              <div
                                key={item?.id || idx}
                                className="p-2 rounded-2 bg-white border d-flex align-items-center gap-2"
                                style={{ borderColor: "#edf2f7" }}
                              >
                                <span className="text-primary small">🏢</span>
                                <span
                                  className="fw-medium text-dark text-truncate"
                                  style={{ fontSize: "13px" }}
                                >
                                  {item?.Company?.name || "--"}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-muted small">
                            No Companies Assigned
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Assigned Customers */}
                <div className={`col-12 ${employeeDetail?.type === 1 ? "col-md-4" : "col-md-6"}`}>
                  <div
                    className="card h-100 border rounded-3 overflow-hidden"
                    style={{ borderColor: "#e2e8f0", background: "#fbfcfe" }}
                  >
                    <div className="card-header bg-white border-bottom py-2 px-3 d-flex align-items-center justify-content-between">
                      <span className="fw-bold text-dark small text-uppercase d-flex align-items-center gap-1">
                        <CustomersIcon />
                        <span>Assigned Customers</span>
                      </span>
                      <span className="badge rounded-pill bg-light text-secondary border">
                        {totalCustomersCount}
                      </span>
                    </div>
                    <div
                      className="card-body p-3"
                      style={{ maxHeight: "240px", overflowY: "auto" }}
                    >
                      {employeeDetail?.usercustomers?.length > 0 ? (
                        <div className="d-flex flex-column gap-2">
                          {employeeDetail.usercustomers.map((userCust, idx) => (
                            <div
                              key={userCust?.id || idx}
                              className="p-2 rounded-2 bg-white border d-flex align-items-center gap-2"
                              style={{ borderColor: "#edf2f7" }}
                            >
                              <span className="text-primary small">🤝</span>
                              <span
                                className="fw-medium text-dark text-truncate"
                                style={{ fontSize: "13px" }}
                              >
                                {userCust?.customer?.name || "--"}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted small">
                          No Customers Assigned
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assigned Sites */}
                <div className={`col-12 ${employeeDetail?.type === 1 ? "col-md-4" : "col-md-6"}`}>
                  <div
                    className="card h-100 border rounded-3 overflow-hidden"
                    style={{ borderColor: "#e2e8f0", background: "#fbfcfe" }}
                  >
                    <div className="card-header bg-white border-bottom py-2 px-3 d-flex align-items-center justify-content-between">
                      <span className="fw-bold text-dark small text-uppercase d-flex align-items-center gap-1">
                        <SitesIcon />
                        <span>Assigned Sites</span>
                      </span>
                      <span className="badge rounded-pill bg-light text-secondary border">
                        {totalSitesCount}
                      </span>
                    </div>
                    <div
                      className="card-body p-3"
                      style={{ maxHeight: "240px", overflowY: "auto" }}
                    >
                      {employeeDetail?.usersites?.length > 0 ? (
                        <div className="d-flex flex-column gap-2">
                          {employeeDetail.usersites.map((userSite, idx) => (
                            <div
                              key={userSite?.id || idx}
                              className="p-2 rounded-2 bg-white border d-flex align-items-center justify-content-between gap-2"
                              style={{ borderColor: "#edf2f7" }}
                            >
                              <div className="d-flex align-items-center gap-2 text-truncate">
                                <span className="text-primary small">📍</span>
                                <span
                                  className="fw-medium text-dark text-truncate"
                                  style={{ fontSize: "13px" }}
                                >
                                  {userSite?.site?.name || "--"}
                                </span>
                              </div>
                              {userSite?.site?.type && (
                                <span
                                  className="badge bg-light text-muted border flex-shrink-0"
                                  style={{ fontSize: "10.5px" }}
                                >
                                  {userSite.site.type}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted small">
                          No Sites Assigned
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 3: Statutory & Banking Details */}
        {(activeTab === "all" || activeTab === "statutory") && (
          <div className="row g-4 mb-4">
            {/* Statutory / Compliance ID Card */}
            <div className="col-12 col-lg-6">
              <div
                className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden"
                style={{ background: "#ffffff" }}
              >
                <div
                  className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom"
                  style={{ borderColor: "#f1f5f9" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-2 p-1 d-flex align-items-center justify-content-center"
                      style={{
                        background: "#fef3c7",
                        color: "#d97706",
                        width: "32px",
                        height: "32px",
                      }}
                    >
                      <span>🛡️</span>
                    </div>
                    <div>
                      <h5
                        className="mb-0 fw-bold text-dark"
                        style={{ fontSize: "15px" }}
                      >
                        Statutory & Compliance IDs
                      </h5>
                      <p
                        className="mb-0 text-muted small"
                        style={{ fontSize: "11.5px" }}
                      >
                        Tax, provident fund, and governmental identifications
                      </p>
                    </div>
                  </div>
                  <span
                    className="badge bg-light text-secondary border px-3 py-1 rounded-pill"
                    style={{ fontSize: "12px", fontWeight: 600 }}
                  >
                    KYC
                  </span>
                </div>

                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          PAN Card Number
                        </div>
                        <div
                          className="fw-bold text-dark font-monospace"
                          style={{ fontSize: "14px", letterSpacing: "0.03em" }}
                        >
                          {employeeDetail?.userdetail?.panNumber || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          Aadhar Card Number
                        </div>
                        <div
                          className="fw-bold text-dark font-monospace"
                          style={{ fontSize: "14px", letterSpacing: "0.03em" }}
                        >
                          {employeeDetail?.userdetail?.aadharNumber || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          UAN Number
                        </div>
                        <div
                          className="fw-semibold text-dark font-monospace"
                          style={{ fontSize: "13.5px" }}
                        >
                          {employeeDetail?.userdetail?.uanNumber || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          PF Number
                        </div>
                        <div
                          className="fw-semibold text-dark font-monospace"
                          style={{ fontSize: "13.5px" }}
                        >
                          {employeeDetail?.userdetail?.pfNumber || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          ESIC Number
                        </div>
                        <div
                          className="fw-semibold text-dark font-monospace"
                          style={{ fontSize: "13.5px" }}
                        >
                          {employeeDetail?.userdetail?.esicNumber || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          Insurance Policy No.
                        </div>
                        <div
                          className="fw-semibold text-dark font-monospace"
                          style={{ fontSize: "13.5px" }}
                        >
                          {employeeDetail?.userdetail?.insuranceNumber || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          Driving License Number
                        </div>
                        <div
                          className="fw-semibold text-dark font-monospace"
                          style={{ fontSize: "13.5px" }}
                        >
                          {employeeDetail?.userdetail?.drivingLicenseNumber ||
                            "--"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Banking & Remittance Credentials Card */}
            <div className="col-12 col-lg-6">
              <div
                className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden"
                style={{ background: "#ffffff" }}
              >
                <div
                  className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom"
                  style={{ borderColor: "#f1f5f9" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-2 p-1 d-flex align-items-center justify-content-center"
                      style={{
                        background: "#eff6ff",
                        color: "#2563eb",
                        width: "32px",
                        height: "32px",
                      }}
                    >
                      <BankIcon />
                    </div>
                    <div>
                      <h5
                        className="mb-0 fw-bold text-dark"
                        style={{ fontSize: "15px" }}
                      >
                        Banking & Remittance Details
                      </h5>
                      <p
                        className="mb-0 text-muted small"
                        style={{ fontSize: "11.5px" }}
                      >
                        Designated bank account for payroll disbursements
                      </p>
                    </div>
                  </div>
                  <span
                    className="badge bg-light text-secondary border px-3 py-1 rounded-pill"
                    style={{ fontSize: "12px", fontWeight: 600 }}
                  >
                    {employeeDetail?.userdetail?.bankName || "Salary Account"}
                  </span>
                </div>

                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          Bank Name
                        </div>
                        <div
                          className="fw-bold text-dark text-truncate"
                          style={{ fontSize: "14px" }}
                        >
                          {employeeDetail?.userdetail?.bankName || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          Account Number
                        </div>
                        <div
                          className="fw-bold text-dark font-monospace text-truncate"
                          style={{ fontSize: "14px", letterSpacing: "0.02em" }}
                        >
                          {employeeDetail?.userdetail?.accountNumber || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          IFSC / RTGS Code
                        </div>
                        <div
                          className="fw-bold text-dark font-monospace"
                          style={{ fontSize: "14px", letterSpacing: "0.03em" }}
                        >
                          {employeeDetail?.userdetail?.ifscCode || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          Account Holder Name
                        </div>
                        <div
                          className="fw-semibold text-dark text-truncate"
                          style={{ fontSize: "13.5px" }}
                        >
                          {employeeDetail?.userdetail?.accountHolderName ||
                            employeeDetail?.name ||
                            "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          Branch Name
                        </div>
                        <div
                          className="fw-semibold text-dark text-truncate"
                          style={{ fontSize: "13.5px" }}
                        >
                          {employeeDetail?.userdetail?.branchName || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6">
                      <div
                        className="p-3 rounded-2 h-100"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase mb-1"
                          style={{ fontSize: "11px", fontWeight: 700 }}
                        >
                          Branch Address
                        </div>
                        <div
                          className="fw-semibold text-dark text-truncate"
                          title={employeeDetail?.userdetail?.branchAddress}
                          style={{ fontSize: "13.5px" }}
                        >
                          {employeeDetail?.userdetail?.branchAddress || "--"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 4: Residential Addresses */}
        {(activeTab === "all" || activeTab === "addresses") && (
          <div className="row g-4 mb-4">
            {/* Current Address */}
            <div className="col-12 col-md-6">
              <div
                className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden"
                style={{ background: "#ffffff" }}
              >
                <div
                  className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom"
                  style={{ borderColor: "#f1f5f9" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-2 p-1 d-flex align-items-center justify-content-center"
                      style={{
                        background: "#eff6ff",
                        color: "#2563eb",
                        width: "32px",
                        height: "32px",
                      }}
                    >
                      <LocationIcon />
                    </div>
                    <h5
                      className="mb-0 fw-bold text-dark"
                      style={{ fontSize: "15px" }}
                    >
                      Current Residential Address
                    </h5>
                  </div>
                  <span
                    className="badge bg-light text-secondary border px-2 py-1 rounded-pill"
                    style={{ fontSize: "11px" }}
                  >
                    Present
                  </span>
                </div>

                <div className="card-body p-4">
                  <div className="mb-3 p-3 rounded-2 bg-light border-0">
                    <div
                      className="text-muted text-uppercase fw-bold mb-1"
                      style={{ fontSize: "11px" }}
                    >
                      Street Address
                    </div>
                    <div
                      className="fw-medium text-dark"
                      style={{ fontSize: "13.5px", lineHeight: "1" }}
                    >
                      {employeeDetail?.userdetail?.address ||
                        "Address not provided"}
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-4">
                      <div
                        className="p-2 rounded-2 text-center"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase"
                          style={{ fontSize: "10px", fontWeight: 700 }}
                        >
                          City
                        </div>
                        <div
                          className="fw-semibold text-dark text-truncate mt-1"
                          style={{ fontSize: "13px" }}
                        >
                          {employeeDetail?.userdetail?.city || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-4">
                      <div
                        className="p-2 rounded-2 text-center"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase"
                          style={{ fontSize: "10px", fontWeight: 700 }}
                        >
                          State
                        </div>
                        <div
                          className="fw-semibold text-dark text-truncate mt-1"
                          style={{ fontSize: "13px" }}
                        >
                          {employeeDetail?.userdetail?.state || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-4">
                      <div
                        className="p-2 rounded-2 text-center"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase"
                          style={{ fontSize: "10px", fontWeight: 700 }}
                        >
                          Postal Code
                        </div>
                        <div
                          className="fw-semibold text-dark font-monospace mt-1"
                          style={{ fontSize: "13px" }}
                        >
                          {employeeDetail?.userdetail?.pinCode || "--"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Permanent Address */}
            <div className="col-12 col-md-6">
              <div
                className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden"
                style={{ background: "#ffffff" }}
              >
                <div
                  className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom"
                  style={{ borderColor: "#f1f5f9" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-2 p-1 d-flex align-items-center justify-content-center"
                      style={{
                        background: "#f8fafc",
                        color: "#475569",
                        width: "32px",
                        height: "32px",
                      }}
                    >
                      <span>🏡</span>
                    </div>
                    <h5
                      className="mb-0 fw-bold text-dark"
                      style={{ fontSize: "15px" }}
                    >
                      Permanent Address
                    </h5>
                  </div>
                  <span
                    className="badge bg-light text-secondary border px-2 py-1 rounded-pill"
                    style={{ fontSize: "11px" }}
                  >
                    Permanent
                  </span>
                </div>

                <div className="card-body p-4">
                  <div className="mb-3 p-3 rounded-2 bg-light border-0">
                    <div
                      className="text-muted text-uppercase fw-bold mb-1"
                      style={{ fontSize: "11px" }}
                    >
                      Street Address
                    </div>
                    <div
                      className="fw-medium text-dark"
                      style={{ fontSize: "13.5px", lineHeight: "1" }}
                    >
                      {employeeDetail?.userdetail?.permanentAddress ||
                        "Permanent address not provided"}
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-4">
                      <div
                        className="p-2 rounded-2 text-center"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase"
                          style={{ fontSize: "10px", fontWeight: 700 }}
                        >
                          City
                        </div>
                        <div
                          className="fw-semibold text-dark text-truncate mt-1"
                          style={{ fontSize: "13px" }}
                        >
                          {employeeDetail?.userdetail?.permanentCity || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-4">
                      <div
                        className="p-2 rounded-2 text-center"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase"
                          style={{ fontSize: "10px", fontWeight: 700 }}
                        >
                          State
                        </div>
                        <div
                          className="fw-semibold text-dark text-truncate mt-1"
                          style={{ fontSize: "13px" }}
                        >
                          {employeeDetail?.userdetail?.permanentState || "--"}
                        </div>
                      </div>
                    </div>

                    <div className="col-4">
                      <div
                        className="p-2 rounded-2 text-center"
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          className="text-muted text-uppercase"
                          style={{ fontSize: "10px", fontWeight: 700 }}
                        >
                          Postal Code
                        </div>
                        <div
                          className="fw-semibold text-dark font-monospace mt-1"
                          style={{ fontSize: "13px" }}
                        >
                          {employeeDetail?.userdetail?.permanentPinCode || "--"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 5: Previous Experience & Verified Documents */}
        {(activeTab === "all" || activeTab === "experience_docs") && (
          <div className="row g-4">
            {/* Previous Work History */}
            <div className="col-12 col-lg-6">
              <div
                className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden"
                style={{ background: "#ffffff" }}
              >
                <div
                  className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom"
                  style={{ borderColor: "#f1f5f9" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-2 p-1 d-flex align-items-center justify-content-center"
                      style={{
                        background: "#eff6ff",
                        color: "#2563eb",
                        width: "32px",
                        height: "32px",
                      }}
                    >
                      <span>💼</span>
                    </div>
                    <h5
                      className="mb-0 fw-bold text-dark"
                      style={{ fontSize: "15px" }}
                    >
                      Prior Work Experience
                    </h5>
                  </div>
                  <span
                    className="badge bg-light text-secondary border px-2 py-1 rounded-pill"
                    style={{ fontSize: "11px" }}
                  >
                    {employeeDetail?.previousworks?.length || 0} Records
                  </span>
                </div>

                <div className="card-body p-4">
                  {employeeDetail?.previousworks &&
                    employeeDetail?.previousworks?.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                      {employeeDetail.previousworks.map((item, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-3 border bg-white shadow-xs position-relative"
                          style={{ borderColor: "#e2e8f0" }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-2 mb-2 flex-wrap">
                            <div>
                              <h6
                                className="mb-0 fw-bold text-dark"
                                style={{ fontSize: "14.5px" }}
                              >
                                {item?.companyName || "Organization"}
                              </h6>
                              <div
                                className="text-secondary small fw-medium mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                {item?.designationName || "Designation"}
                              </div>
                            </div>
                            <span
                              className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle px-2 py-1"
                              style={{ fontSize: "10px" }}
                            >
                              {CalculateDuration(item?.fromDate, item?.toDate)}
                            </span>
                          </div>

                          <div
                            className="text-muted small d-flex align-items-center gap-1 pt-2 border-top"
                            style={{ fontSize: "11.5px", borderColor: "#f1f5f9" }}
                          >
                            <span>📅</span>
                            <span>
                              {moment(item?.fromDate).format("DD MMM YYYY")} –{" "}
                              {item?.toDate
                                ? moment(item.toDate).format("DD MMM YYYY")
                                : "Currently Working"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                        style={{
                          width: "48px",
                          height: "48px",
                          background: "#f8fafc",
                          fontSize: "20px",
                        }}
                      >
                        💼
                      </div>
                      <div className="fw-semibold text-dark small">
                        No Previous Work Records
                      </div>
                      <div className="text-muted small mt-1">
                        Previous organizational experience has not been added
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Verified Documents Gallery */}
            <div className="col-12 col-lg-6">
              <div
                className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden"
                style={{ background: "#ffffff" }}
              >
                <div
                  className="card-header bg-white py-3 px-4 d-flex align-items-center justify-content-between border-bottom"
                  style={{ borderColor: "#f1f5f9" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-2 p-1 d-flex align-items-center justify-content-center"
                      style={{
                        background: "#eff6ff",
                        color: "#2563eb",
                        width: "32px",
                        height: "32px",
                      }}
                    >
                      <DocumentIcon />
                    </div>
                    <h5
                      className="mb-0 fw-bold text-dark"
                      style={{ fontSize: "15px" }}
                    >
                      Verified Documents & Credentials
                    </h5>
                  </div>
                  <span
                    className="badge bg-light text-secondary border px-2 py-1 rounded-pill"
                    style={{ fontSize: "11px" }}
                  >
                    {totalDocumentsCount} Files
                  </span>
                </div>

                <div className="card-body p-4">
                  {employeeDetail?.documents?.length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {employeeDetail.documents.map((doc, index) => {
                        const dateFormatted =
                          doc?.updatedAt || doc?.createdAt
                            ? moment(doc.updatedAt || doc.createdAt).format(
                              "DD MMM YYYY, h:mm a"
                            )
                            : "";
                        return (
                          <div
                            key={index}
                            className="p-2 px-3 rounded-2 border bg-white d-flex align-items-center justify-content-between gap-3 transition-all"
                            style={{
                              borderColor: "#e2e8f0",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 text-truncate">
                              <div
                                className="rounded-2 p-1 d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                  background: "#f0fdf4",
                                  color: "#16a34a",
                                  width: "32px",
                                  height: "32px",
                                }}
                              >
                                <DocumentIcon />
                              </div>
                              <div className="text-truncate">
                                <div
                                  className="fw-semibold text-dark text-truncate"
                                  style={{ fontSize: "13.5px" }}
                                  title={doc?.name}
                                >
                                  {doc?.name || "Uploaded Document"}
                                </div>
                                {dateFormatted && (
                                  <div
                                    className="text-muted small mt-1"
                                    style={{ fontSize: "11px" }}
                                  >
                                    Uploaded: {dateFormatted}
                                  </div>
                                )}
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1 flex-shrink-0"
                              onClick={() => handleDocumentClick(doc)}
                              style={{ fontSize: "12px" }}
                            >
                              <ViewIcon />
                              <span>View</span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                        style={{
                          width: "48px",
                          height: "48px",
                          background: "#f8fafc",
                          fontSize: "20px",
                        }}
                      >
                        📁
                      </div>
                      <div className="fw-semibold text-dark small">
                        No Documents Uploaded
                      </div>
                      <div className="text-muted small mt-1">
                        Employee verification documents will be displayed here
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ViewDocumentModal
        modal={modal}
        toggle={toggleModal}
        selectedDocument={selectedDocument}
      />
    </>
  );
}
